import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import get from 'lodash/get';

import AxiosClient from 'configurations/api-client';
import authActions from 'actions/auth-actions';
import { getErrorMessage } from 'utils/message';
import { LOCKED_STATUS } from 'constants/common';
import { currentSiteSelector } from 'selectors/root-site';
import { isMfaEnabledSelector } from 'selectors/auth';

export const API_MFA_CHALLENGE_STEP_UP = '/mfa_challenge/step_up';
export const API_MFA_CHALLENGE_VERIFICATION = '/mfa_challenge/verification';
export const API_MFA_CHALLENGE_ENROLLMENT = '/mfa_challenge/enrollment';
export const API_MFA_CHALLENGE_ENROLLMENT_CONFIRMATION = '/mfa_challenge/enrollment/confirmation';
export const API_MFA_SETTING = '/mfa_setting';
export const API_MFA_SETTING_CONFIRMATION = '/mfa_setting/confirmation';
export const API_MFA_SETTING_BACKUP_CODES = '/mfa_setting/backup_codes';
export const API_MFA_SETTING_TRUSTED_DEVICES = '/mfa_setting/trusted_devices';
export const API_MFA_REVOCATION = '/mfa_revocation';
export const MFA_CHALLENGE_TOKEN_HEADER = 'Mfa-Challenge-Token';
export const MFA_SETTING_QUERY_KEY = 'mfaSetting';

export const MFA_METHODS = { totp: 'totp', backupCode: 'backup_code' };
export const MFA_POLICIES = { disabled: 'disabled', optional: 'optional', required: 'required' };

export const getMfaErrorMessage = (response) => get(response, 'data.message') || getErrorMessage(response);

// An expired / invalid challenge (401) or an account locked after the password step (423) cannot
// be answered on the code step; the sign-in page goes back to the credentials form with the message
export const isMfaChallengeOver = (response) => [401, LOCKED_STATUS].includes(get(response, 'status'));

// A `disabled` site hides two-factor from users who don't have it, but an account that already
// has it on is still challenged everywhere and must be able to manage its codes here
export const useMfaSectionVisible = (siteSlug) => {
  const { mfaPolicy } = useSelector(currentSiteSelector(siteSlug));
  const isMfaEnabled = useSelector(isMfaEnabledSelector);

  return mfaPolicy !== MFA_POLICIES.disabled || isMfaEnabled;
};

const siteParams = (siteId) => ({ params: { siteId } });
const challengeHeaders = (mfaToken) => ({ headers: { [MFA_CHALLENGE_TOKEN_HEADER]: mfaToken } });

// A step-up 403 only says the site needs a code; the challenge is issued once the code step opens
export const useIssueMfaStepUpMutation = () =>
  useMutation({
    mutationFn: (siteId) =>
      AxiosClient.post(API_MFA_CHALLENGE_STEP_UP, {}, siteParams(siteId)).then((response) => response.data),
  });

export const useVerifyMfaChallengeMutation = () =>
  useMutation({
    mutationFn: ({ mfaToken, code, method, rememberMe, trustDevice }) =>
      AxiosClient.post(
        API_MFA_CHALLENGE_VERIFICATION,
        { mfaChallenge: { code, method, rememberMe, trustDevice } },
        challengeHeaders(mfaToken),
      ).then((response) => response.data),
  });

export const useStartMfaChallengeEnrollmentMutation = () =>
  useMutation({
    mutationFn: ({ mfaToken }) =>
      AxiosClient.post(API_MFA_CHALLENGE_ENROLLMENT, {}, challengeHeaders(mfaToken)).then((response) => response.data),
  });

export const useConfirmMfaChallengeEnrollmentMutation = () =>
  useMutation({
    mutationFn: ({ mfaToken, code, rememberMe }) =>
      AxiosClient.post(
        API_MFA_CHALLENGE_ENROLLMENT_CONFIRMATION,
        { mfaChallenge: { code, rememberMe } },
        challengeHeaders(mfaToken),
      ).then((response) => response.data),
  });

// The link from the "two-factor was turned on" email is the whole credential: no session, no site
export const useRevokeMfaMutation = () =>
  useMutation({
    mutationFn: (token) =>
      AxiosClient.post(API_MFA_REVOCATION, { mfaRevocation: { token } }).then((response) => response.data),
  });

export const useGetMfaSettingQuery = (siteId) =>
  useQuery({
    queryKey: [MFA_SETTING_QUERY_KEY, siteId],
    queryFn: () =>
      AxiosClient.get(API_MFA_SETTING, { ...siteParams(siteId), data: null }).then((response) => response.data),
    enabled: !!siteId,
    staleTime: 15 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStartMfaEnrollmentMutation = (siteId) =>
  useMutation({
    mutationFn: () => AxiosClient.post(API_MFA_SETTING, {}, siteParams(siteId)).then((response) => response.data),
    onError: (error) => toast.error(getErrorMessage(error.response)),
  });

const useRefreshSession = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return ({ token }) => {
    dispatch(authActions.refreshToken(token));
    queryClient.invalidateQueries({ queryKey: [MFA_SETTING_QUERY_KEY] });
  };
};

export const useConfirmMfaEnrollmentMutation = (siteId) => {
  const refreshSession = useRefreshSession();

  return useMutation({
    mutationFn: (code) =>
      AxiosClient.post(API_MFA_SETTING_CONFIRMATION, { mfaSetting: { code } }, siteParams(siteId)).then(
        (response) => response.data,
      ),
    onSuccess: refreshSession,
  });
};

export const useDisableMfaMutation = (siteId) => {
  const refreshSession = useRefreshSession();

  return useMutation({
    mutationFn: (reauth) =>
      AxiosClient.delete(API_MFA_SETTING, { data: { mfaSetting: reauth }, ...siteParams(siteId) }).then(
        (response) => response.data,
      ),
    onSuccess: (data) => {
      refreshSession(data);
      toast.success('Two-factor authentication is turned off.');
    },
  });
};

export const useRegenerateMfaBackupCodesMutation = (siteId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reauth) =>
      AxiosClient.post(API_MFA_SETTING_BACKUP_CODES, { mfaSetting: reauth }, siteParams(siteId)).then(
        (response) => response.data,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MFA_SETTING_QUERY_KEY] }),
  });
};

// Nested under the setting's key so turning two-factor on or off, which forgets every device, refetches the list
export const useGetMfaTrustedDevicesQuery = (siteId) =>
  useQuery({
    queryKey: [MFA_SETTING_QUERY_KEY, siteId, 'trustedDevices'],
    queryFn: () =>
      AxiosClient.get(API_MFA_SETTING_TRUSTED_DEVICES, { ...siteParams(siteId), data: null }).then(
        (response) => response.data.data,
      ),
    enabled: !!siteId,
    refetchOnWindowFocus: false,
  });

export const useForgetMfaTrustedDeviceMutation = (siteId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => AxiosClient.delete(`${API_MFA_SETTING_TRUSTED_DEVICES}/${id}`, siteParams(siteId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MFA_SETTING_QUERY_KEY, siteId, 'trustedDevices'] });
      toast.success('Device forgotten. It will ask for your code at the next sign-in.');
    },
    onError: (error) => toast.error(getMfaErrorMessage(error.response)),
  });
};
